/**
 * DetectiveStory Page
 *
 * The main Story Detective experience:
 * 1. Read the story
 * 2. Answer questions about story structure
 * 3. See results and explanations
 */

import { useState, useEffect, useRef } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  Home,
  BookOpen,
} from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import {
  StoryReader,
  QuestionCard,
  QuestionResultCard,
  ScoreDisplay,
  ProgressDots,
} from '@/components/detective'
import { useFamily } from '@/context/FamilyContext'
import { useToast } from '@/context/ToastContext'
import {
  getDetectiveStory,
  startAttempt,
  submitAttempt,
  parseQuestions,
  parseAnswerKey,
  getScoreLabel,
  hasAttemptedToday,
  type DetectiveQuestion,
  type AttemptResult,
} from '@/services/detectiveService'
import type { DetectiveStory, DetectiveAttempt } from '@/types/database'

type Phase = 'reading' | 'quiz' | 'results'

export function DetectiveStory() {
  const { storyId } = useParams<{ storyId: string }>()
  const navigate = useNavigate()
  const { activeWriter } = useFamily()
  const { showToast } = useToast()
  const startTimeRef = useRef<number>(0)

  // Data state
  const [story, setStory] = useState<DetectiveStory | null>(null)
  const [attempt, setAttempt] = useState<DetectiveAttempt | null>(null)
  const [questions, setQuestions] = useState<DetectiveQuestion[]>([])
  const [result, setResult] = useState<AttemptResult | null>(null)

  // UI state
  const [phase, setPhase] = useState<Phase>('reading')
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [answers, setAnswers] = useState<Record<number, number>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [alreadyAttempted, setAlreadyAttempted] = useState(false)

  // Load story and check attempt status
  useEffect(() => {
    async function loadData() {
      if (!storyId || !activeWriter) return

      setIsLoading(true)
      try {
        // Load story
        const storyData = await getDetectiveStory(storyId)
        if (!storyData) {
          showToast('Story not found', 'error')
          navigate('/detective')
          return
        }
        setStory(storyData)
        setQuestions(parseQuestions(storyData))

        // Check if already attempted today
        const attempted = await hasAttemptedToday(activeWriter.id, storyId)
        setAlreadyAttempted(attempted)
      } catch (error) {
        console.error('Error loading story:', error)
        showToast('Failed to load story', 'error')
        navigate('/detective')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [storyId, activeWriter, navigate, showToast])

  // Handle finishing reading and starting quiz
  const handleStartQuiz = async () => {
    if (!story || !activeWriter) return

    if (alreadyAttempted) {
      showToast("You've already tried this story today. Come back tomorrow!", 'error')
      return
    }

    try {
      const newAttempt = await startAttempt(activeWriter.id, story.id)
      setAttempt(newAttempt)
      setPhase('quiz')
      startTimeRef.current = Date.now()
    } catch (error) {
      console.error('Error starting attempt:', error)
      if ((error as Error).message === 'Already attempted this story today') {
        showToast("You've already tried this story today!", 'error')
        setAlreadyAttempted(true)
      } else {
        showToast('Failed to start quiz', 'error')
      }
    }
  }

  // Handle answer selection
  const handleSelectAnswer = (answerIndex: number) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion]: answerIndex,
    }))
  }

  // Handle navigation
  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1)
    }
  }

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1)
    }
  }

  // Handle quiz submission
  const handleSubmitQuiz = async () => {
    if (!attempt || !story) return

    // Check if all questions answered
    if (Object.keys(answers).length < questions.length) {
      showToast('Please answer all questions first', 'error')
      return
    }

    setIsSubmitting(true)
    try {
      const timeTaken = Math.round((Date.now() - startTimeRef.current) / 1000)
      const attemptResult = await submitAttempt(attempt.id, answers, timeTaken, story)
      setResult(attemptResult)
      setPhase('results')
    } catch (error) {
      console.error('Error submitting quiz:', error)
      showToast('Failed to submit answers', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle try another story
  const handleTryAnother = () => {
    navigate('/detective')
  }

  // Loading state
  if (isLoading || !story) {
    return (
      <div className="min-h-screen bg-[var(--color-kids-bg)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-kids-primary)]" />
      </div>
    )
  }

  const answerKey = parseAnswerKey(story)
  const allAnswered = Object.keys(answers).length === questions.length
  const scoreInfo = result ? getScoreLabel(result.score, result.totalQuestions) : null

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)] pb-20">
      {/* Header */}
      <header className="bg-[var(--color-kids-surface)] border-b border-[var(--color-kids-surface-alt)] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/detective">
              <Button variant="ghost" size="sm">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex-1 min-w-0">
              <h1 className="font-display font-bold text-lg text-[var(--color-kids-text)] truncate">
                {story.title}
              </h1>
              <p className="text-sm text-[var(--color-kids-text-muted)]">
                {phase === 'reading' && 'Read the story carefully'}
                {phase === 'quiz' && `Question ${currentQuestion + 1} of ${questions.length}`}
                {phase === 'results' && 'Quiz Complete!'}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* Phase 1: Reading */}
          {phase === 'reading' && (
            <motion.div
              key="reading"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <StoryReader
                story={story}
                onFinishReading={handleStartQuiz}
                showFinishButton={!alreadyAttempted}
              />

              {alreadyAttempted && (
                <Card className="mt-4">
                  <CardContent className="p-4 text-center">
                    <p className="text-[var(--color-kids-text)] mb-3">
                      You've already tried this story today!
                    </p>
                    <Button onClick={handleTryAnother}>
                      Try Another Story
                    </Button>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}

          {/* Phase 2: Quiz */}
          {phase === 'quiz' && questions[currentQuestion] && (
            <motion.div
              key="quiz"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Progress */}
              <div className="mb-6">
                <ProgressDots
                  total={questions.length}
                  current={currentQuestion}
                  answers={answers}
                />
              </div>

              {/* Question */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentQuestion}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <QuestionCard
                    question={questions[currentQuestion]}
                    questionNumber={currentQuestion + 1}
                    totalQuestions={questions.length}
                    selectedAnswer={answers[currentQuestion] ?? null}
                    onSelect={handleSelectAnswer}
                  />
                </motion.div>
              </AnimatePresence>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-6">
                <Button
                  variant="ghost"
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0}
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Previous
                </Button>

                {currentQuestion < questions.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    disabled={answers[currentQuestion] === undefined}
                  >
                    Next
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmitQuiz}
                    disabled={!allAnswered || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      'See Results'
                    )}
                  </Button>
                )}
              </div>

              {/* Story reference */}
              <div className="mt-6">
                <StoryReader story={story} showFinishButton={false} />
              </div>
            </motion.div>
          )}

          {/* Phase 3: Results */}
          {phase === 'results' && result && scoreInfo && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Score */}
              <Card className="mb-6">
                <CardContent className="p-6">
                  <ScoreDisplay
                    score={result.score}
                    total={result.totalQuestions}
                    label={scoreInfo.label}
                    emoji={scoreInfo.emoji}
                    color={scoreInfo.color}
                  />
                </CardContent>
              </Card>

              {/* Question Results */}
              <h2 className="font-display font-bold text-lg text-[var(--color-kids-text)] mb-4">
                Your Answers
              </h2>
              <div className="space-y-4 mb-6">
                {questions.map((question, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <QuestionResultCard
                      question={question}
                      questionNumber={index + 1}
                      userAnswer={answers[index] ?? null}
                      explanation={answerKey[question.element]}
                    />
                  </motion.div>
                ))}
              </div>

              {/* Actions */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="ghost"
                      onClick={() => setPhase('reading')}
                      className="flex-1"
                    >
                      <BookOpen className="w-5 h-5 mr-2" />
                      Read Again
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleTryAnother}
                      className="flex-1"
                    >
                      <RefreshCw className="w-5 h-5 mr-2" />
                      Try Another
                    </Button>
                    <Link to="/detective" className="flex-1">
                      <Button className="w-full">
                        <Home className="w-5 h-5 mr-2" />
                        Back to Hub
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>

              {/* Story reference collapsed */}
              <div className="mt-6">
                <StoryReader story={story} showFinishButton={false} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  )
}

export default DetectiveStory
